// src/stores/globalFilterStore.ts
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { DEFAULT_FILTER_MODE, type FilterMode } from '../types/filters.types'

export type GlobalMemberOption = { id: string; name: string; image?: string | null }
export type GlobalMember = GlobalMemberOption | null
export type GlobalBrand = { id: string; name: string; logo?: string | null }

interface GlobalFilterState {
  member: GlobalMember
  brands: GlobalBrand[]
  // CORE-2817: each field is either an include filter or an exclude filter,
  // never both — selecting B1+B2 already yields only B1+B2, so additionally
  // excluding B3 would be a no-op. The mode picks which value slot below is
  // live and which query param the page emits.
  memberMode: FilterMode
  brandMode: FilterMode
  excludedMembers: GlobalMemberOption[]
  excludedBrands: GlobalBrand[]
  // Monotonic counter bumped on every user-driven change (setMember/setBrands/
  // the mode + exclusion setters/reset). Pages compare it against the last
  // version they applied: a change means "the user just set the global filter"
  // → re-apply to every page, overriding any manual per-page edits made since
  // the previous global change.
  version: number
  setMember: (m: GlobalMember) => void
  setBrands: (b: GlobalBrand[]) => void
  setMemberMode: (mode: FilterMode) => void
  setBrandMode: (mode: FilterMode) => void
  setExcludedMembers: (m: GlobalMemberOption[]) => void
  setExcludedBrands: (b: GlobalBrand[]) => void
  reset: () => void
}

export const useGlobalFilterStore = create<GlobalFilterState>()(
  devtools(
    persist(
      (set) => ({
        member: null,
        brands: [],
        memberMode: DEFAULT_FILTER_MODE,
        brandMode: DEFAULT_FILTER_MODE,
        excludedMembers: [],
        excludedBrands: [],
        version: 0,
        setMember: (member) => set((s) => ({ member, version: s.version + 1 })),
        setBrands: (brands) => set((s) => ({ brands, version: s.version + 1 })),
        setMemberMode: (memberMode) => set((s) => ({ memberMode, version: s.version + 1 })),
        setBrandMode: (brandMode) => set((s) => ({ brandMode, version: s.version + 1 })),
        setExcludedMembers: (excludedMembers) => set((s) => ({ excludedMembers, version: s.version + 1 })),
        setExcludedBrands: (excludedBrands) => set((s) => ({ excludedBrands, version: s.version + 1 })),
        reset: () =>
          set((s) => ({
            member: null,
            brands: [],
            memberMode: DEFAULT_FILTER_MODE,
            brandMode: DEFAULT_FILTER_MODE,
            excludedMembers: [],
            excludedBrands: [],
            version: s.version + 1,
          })),
      }),
      {
        name: 'global-filter-storage',
        // Users who stored this before CORE-2817 have no mode/exclusion keys.
        // `merge` runs before the state is first read, so backfill defaults here
        // rather than defending at every read site.
        merge: (persisted, current) => {
          const saved = (persisted ?? {}) as Partial<GlobalFilterState>
          return {
            ...current,
            ...saved,
            memberMode: saved.memberMode ?? DEFAULT_FILTER_MODE,
            brandMode: saved.brandMode ?? DEFAULT_FILTER_MODE,
            excludedMembers: saved.excludedMembers ?? [],
            excludedBrands: saved.excludedBrands ?? [],
            brands: saved.brands ?? [],
          }
        },
      }
    ),
    { name: 'GlobalFilterStore', enabled: import.meta.env.DEV }
  )
)
